import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { RavenService, type TrackEventPayload } from '../raven-service';
import { RavenInsertMeta, sentryDeferred } from '@telecom/shared/startup/sentry';
import { Logger } from '@patsnap/tahiti-core';
import { TrackerApi } from '../../../infrastructure/api-functions/tracker-api';
import { UserInfoService } from '../../../domain/permission';
import { InitCookie } from '@patsnap/init-cookie';

const MODULE_NAME = 'EUREKA';

@injectable()
export class RavenServiceImpl implements RavenService {

    constructor(
        @inject(Logger)
        private readonly logger: Logger,
        @inject(TrackerApi)
        private readonly trackerApi: TrackerApi,
        @inject(UserInfoService)
        private readonly userInfoService: UserInfoService,
    ) {}

    async track(eventCode: string, data: Partial<TrackEventPayload>): Promise<void> {
        if (!data.eventType) {
            console.error('RavenServiceImpl.track missing eventType !');
        }

        if (import.meta.env.DEV) {
            this._log(eventCode, data);
            return;
        }

        const { eventType, sourceType = eventCode, otherParam, eventDetailStr } = data;

        const userLevel = await this.userInfoService.userLevel();
        const signatureId = InitCookie.getVisitorId();
        const campaignName = InitCookie.getCampaignName();

        await this.trackerApi.trackEvent({
            // 默认传 'SS AI Drafting' 以兼容老数据分析
            event_type: eventType || 'SS AI Drafting',
            source_type: sourceType,
            other_param: otherParam,
            event_detail_str: this._stringify({
                // 用户的等级
                userLevel,
                // 游客版的唯一ID
                signatureId,
                // 用户第一次从哪个渠道进来的
                campaignName,
                // 产品线,有些事件其他产品线也会埋,这几加一个区分
                product_line: 'eureka_ip',
                // 存下页面地址
                pageUrl: window.location.href,

                ...(eventDetailStr || {}),
            }),
            module_type: MODULE_NAME,
        }).catch((e) => {
            this.logger.warn(`track event failed: ${e}`);
        });
    }

    async rvTrack(eventCode: string, data: TrackEventPayload): Promise<void> {
        await sentryDeferred.promise;
        if (import.meta.env.DEV) {
            this._log(eventCode, data);
            return;
        }
        RavenInsertMeta(MODULE_NAME, eventCode, data);
    }

    private _stringify(data?: Record<string, unknown>) {
        if (!data) return undefined;
        try {
            return JSON.stringify(data);
        } catch (e) {
            return undefined;
        }
    }

    private _log(eventCode: string, data: Partial<TrackEventPayload>): void {
        const logs = [
            `%cRaven%cService%c > %c${MODULE_NAME}%c > %c${eventCode}`,
            'color: white;background-color: #1976D2;padding: 2px 0 2px 4px;',
            'color: white;background-color: #45A321;padding: 2px 4px 2px 0;',
            '',
            'color: white;background-color: #dc7c08;padding: 2px 4px; border-radius: 4px;',
            '',
            'color: #1976D2;font-weight: bold;',
            data,
        ];
        this.logger.info(...logs);
    }
}
